import { createSelector } from '@reduxjs/toolkit';

import { RelationshipMapping } from 'src/containers/ProjectDiagram/types/data';
import { LinkData, NodeData } from 'src/containers/ProjectDiagram/types/model';
import {
  isMapping,
  isModelConceptRelationship,
  isRelationshipMapping,
} from 'src/containers/ProjectDiagram/utils/data';
import {
  DomainsAndRanges,
  addDomainsAndRanges,
  addDomainsAndRangesFromHierarchy,
  isValidMappingDomainAndRange,
} from 'src/containers/ProjectDiagram/utils/isValidDomainAndRange';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import {
  prevProjectLinkDataArraySelector,
  projectLinkDataArraySelector,
} from 'src/selectors/projectLinkDataArray';
import {
  prevProjectNodeDataArraySelector,
  projectNodeDataArraySelector,
} from 'src/selectors/projectNodeDataArray';

type RelationshipMappingFilter = {
  relationshipIri: string;
  sourceKey?: string;
  targetKey?: string;
  sourceIri?: string;
  targetIri?: string;
};

const filterRelationshipMappings =
  (filter?: RelationshipMappingFilter) =>
  (nodeDataArray: NodeData[], linkDataArray: LinkData[]) => {
    const classMappingIriByKey: Map<string, string> = new Map();
    const domainsAndRanges: DomainsAndRanges = new Map();

    linkDataArray.forEach((linkData) => {
      if (isMapping(linkData)) {
        classMappingIriByKey.set(
          linkData.key,
          getModelConceptClassValue(linkData.to || '')
        );
      } else if (isModelConceptRelationship(linkData)) {
        addDomainsAndRanges(domainsAndRanges)(linkData);
      }
    });

    addDomainsAndRangesFromHierarchy(
      { nodeDataArray, linkDataArray },
      domainsAndRanges
    );

    return linkDataArray.filter((linkData): linkData is RelationshipMapping => {
      // ignore things that are not relationship mappings
      if (!isRelationshipMapping(linkData)) {
        return false;
      }

      // no need to filter if there are no filters
      if (!filter) {
        return true;
      }

      // return early if the relationship iri does not match
      if (linkData.relationshipIri !== filter.relationshipIri) {
        return false;
      }

      if (
        !isValidMappingDomainAndRange(
          linkData,
          classMappingIriByKey,
          domainsAndRanges
        )
      ) {
        console.warn(`Invalid relationship mapping (id: ${linkData.value})`);
        return false;
      }

      if (
        filter.sourceIri &&
        // the source iri matches the relationship mapping identifier class iri
        ((linkData.fromClassIri &&
          linkData.fromClassIri === filter.sourceIri) ||
          // the source iri matches the linked source class mapping iri
          (linkData.from &&
            filter.sourceIri === classMappingIriByKey.get(linkData.from)))
      ) {
        return true;
      }

      if (
        filter.targetIri &&
        // the target iri matches the relationship mapping identifier class iri
        ((linkData.toClassIri && linkData.toClassIri === filter.targetIri) ||
          // the target iri matches the linked target class mapping iri
          (linkData.to &&
            filter.targetIri === classMappingIriByKey.get(linkData.to)))
      ) {
        return true;
      }

      // the source key matches the relationship mapping source
      if (filter.sourceKey && filter.sourceKey === linkData.from) {
        return true;
      }

      // the target key matches the relationship mapping target
      if (filter.targetKey && filter.targetKey === linkData.to) {
        return true;
      }

      // match if the relationship iri is the only filter
      return (
        !filter.sourceIri &&
        !filter.targetIri &&
        !filter.sourceKey &&
        !filter.targetKey
      );
    });
  };

export const projectRelationshipMappingsSelector = (
  projectId: string,
  filter?: RelationshipMappingFilter
) =>
  createSelector(
    projectNodeDataArraySelector(projectId),
    projectLinkDataArraySelector(projectId),
    filterRelationshipMappings(filter)
  );

export const prevProjectRelationshipMappingsSelector = (
  projectId: string,
  filter?: RelationshipMappingFilter
) =>
  createSelector(
    prevProjectNodeDataArraySelector(projectId),
    prevProjectLinkDataArraySelector(projectId),
    filterRelationshipMappings(filter)
  );
